import { useState } from 'react';
import { Socket } from 'socket.io-client';

interface StandbyProps {
  sessionId: string | null;
  unitName: string | null;
  crewName: string | null;
  onRegister: (unitName: string, crewName: string) => void;
  socket: Socket | null;
}

export default function Standby({ sessionId, unitName, crewName, onRegister, socket: _socket }: StandbyProps) {
  const [inputUnitName, setInputUnitName] = useState('');
  const [san1, setSan1] = useState('');
  const [san2, setSan2] = useState('');
  const [san3, setSan3] = useState('');
  const [isAwaitingAlarm, setIsAwaitingAlarm] = useState(Boolean(unitName && crewName));

  const canRegister = inputUnitName.trim() !== '' && san1.trim() !== '';

  const handleRegister = () => {
    if (!canRegister) return;
    const crew = [
      san1.trim() ? `SAN1: ${san1.trim()}` : '',
      san2.trim() ? `SAN2: ${san2.trim()}` : '',
      san3.trim() ? `SAN3: ${san3.trim()}` : '',
    ]
      .filter(Boolean)
      .join(' | ');
    onRegister(inputUnitName.trim(), crew);
    setIsAwaitingAlarm(true);
    setInputUnitName('');
    setSan1('');
    setSan2('');
    setSan3('');
  };

  if (!isAwaitingAlarm) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 space-y-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-2">👥 Einheit registrieren</h1>
          <p className="text-gray-400">Fahrzeugname und Besatzung eingeben</p>
        </div>

        <div className="w-full max-w-sm space-y-5">
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-300">Fahrzeug</label>
            <input
              type="text"
              placeholder="z.B. RTW 1"
              value={inputUnitName}
              onChange={(e) => setInputUnitName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleRegister()}
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 text-lg"
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-300">Besatzung</label>
            <div className="space-y-2">
              {[
                { label: 'SAN 1', value: san1, setter: setSan1, required: true },
                { label: 'SAN 2', value: san2, setter: setSan2, required: false },
                { label: 'SAN 3', value: san3, setter: setSan3, required: false },
              ].map(({ label, value, setter, required }) => (
                <div key={label} className="flex items-center gap-3">
                  <span className="text-sm font-bold text-gray-300 w-12 shrink-0">{label}</span>
                  <input
                    type="text"
                    placeholder={required ? 'Name (erforderlich)' : 'Name (optional)'}
                    value={value}
                    onChange={(e) => setter(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleRegister()}
                    className="flex-1 px-4 py-3 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 text-base"
                  />
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={handleRegister}
            disabled={!canRegister}
            className="w-full bg-dispatch-blue hover:bg-blue-700 disabled:bg-gray-600 text-white font-bold py-3 px-4 rounded text-lg transition"
          >
            ✓ Einheit registrieren
          </button>
        </div>

        <div className="text-center text-xs text-gray-500">
          <p>Session ID: {sessionId}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 space-y-8">
      <div className="text-center">
        <h1 className="text-4xl font-bold mb-2">🟢</h1>
        <h2 className="text-2xl font-bold mb-1">{unitName}</h2>
        <p className="text-gray-300 font-semibold">Status: Frei Wache</p>
      </div>

      <div className="w-full max-w-sm space-y-4">
        <div className="bg-gray-800 p-6 rounded-lg text-center border-2 border-green-600">
          <p className="text-xl font-bold">📍 Frei Wache</p>
          <p className="text-sm text-gray-400 mt-2">Warten auf Alarmierung von der Leitstelle...</p>
        </div>

        <div className="bg-gray-800 p-4 rounded-lg text-center text-xs text-gray-400">
          <p>Lautstärke: 🔊 Max</p>
          <p>Vibrationen: ✓ Ein</p>
          <p>Push-Notifications: ✓ Ein</p>
        </div>
      </div>

      <button
        onClick={() => window.location.reload()}
        className="text-xs text-gray-500 hover:text-gray-300 transition"
      >
        ↺ Neu registrieren
      </button>
    </div>
  );
}
